define('bindingHandlers/timeInput/timeInputBinding',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'lib/globalize',
    'moment',

    'viewModels/ViewModel',
    'bindingHandlers/timeInput/TimeInputView',

    'extensions/i18n/knockout.i18n',
    'bindingHandlers/include'

], function(
    $,
    _,
    ko,
    globalize,
    moment,

    ViewModel,
    TimeInputView

    ){
    "use strict";

    var TIME_INPUT_TEMPLATE        = 'bindingHandlers/timeInput/template',
        TIME_INPUT_NAMESPACE       = '.timeInputWidget',
        EVENT_FOCUS                = 'focus',
        EVENT_MOUSE_OVER           = 'mouseover',
        EVENT_MOUSE_OUT            = 'mouseout',
        EVENT_CLICK                = 'click';

    /**
     * Cross browser time input widget - allows users to enter locale formatted time that is constrained to input only timelike
     * information.  The widget has spinner buttons to change values and supports value mutation by direction arrows, pageUp/down
     * and home/end.  If the time locale format specifies AM/PM, the localized string that represents AM/PM is used to determine
     * which value to set.  The user can type in the localized version of the string or use the arrow buttons, and spinners to set
     * the values.  The value binding is two-way.
     *
     * Usage - the binding handler should be set on a text input.  The text input will be used as the background of the time input.
     * The bindings for hasFocus, visible, and disable are transferred to the inner time widget.  If any of those properties are
     * set the widget receives those bindings.  If the bindings are live they are maintained.
     *
     * When used with a string time:
     * @param value {String} - OBSERVABLE - time formatted like 'HH:mm:ss' - this is not the display time.  This is just the time that will be
     * parsed by the internal moment object.  The display time will be the localized display time for the current user locale.
     * @param useStringValue { Boolean } - if this is true the string value will be set and created for the time.  This is necessary
     * when the component is unset to ensure a string value will be set to the value binding instead of a moment.  If this is not true
     * a moment value will be returned and set in the value binding.
     *
     * When used with a moment object:
     * @param value {Object} - OBSERVABLE - moment object that can be in any time zone offset.  The changes made to the hours minutes and seconds
     * will be in the current moment timezone.
     *
     * @param useLongFormat {Boolean} - If true the displayed time will be using the long format time with hours minutes, and seconds.  If false
     * the displayed time will be the short format of just hours and minutes.
     *
     * @example - timeString is an observable string '13:24' which will display "1 : 24 PM" in the standard locale
     *
     *   <div data-bind=" timeInput : timeStringObservable "></div>
     *
     *   <div data-bind=" timeInput : {
     *        value:timeStringObservable,
     *        useLongFormat:true
     *   }"></div>
     *
     *
     * @example - timeMoment is an observable moment('2014-10-24T18:55:22:012') which will display the moment time in the current locale ( en-fr ) "18 : 55 : 22"
     *
     *   <div data-bind=" timeInput : timeMomentObservable "></div>
     *
     *   <div data-bind=" timeInput : {
     *        value:timeMomentObservable,
     *        useLongFormat:true
     *   }"></div>
     */

    ko.bindingHandlers.timeInput = {
        init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
            var self = this,
                $backgroundInput = $( element ),

                val = ko.unwrap(valueAccessor()),

                options = _.isPlainObject( val ) ? val : { value : valueAccessor() },
                valueIsString = _.isString( val ) || ( _.has( options, 'value' ) && _.isString( ko.unwrap( options.value ) )),

                subscriptions = [],

                currentCulture = globalize.culture(),
                timeInputView = new TimeInputView( _.defaults( options, {
                    value             : ko.observable( moment() ), // The value by default is a moment - but it can be a string in the form of HH:mm:ss
                    useStringValue    : valueIsString,
                    delimiter         : currentCulture.calendar[':'],
                    isDisabled        : $backgroundInput.attr( 'readonly') || $backgroundInput.attr( 'disabled'),
                    momentFormat      : options.useLongFormat ?
                        currentCulture.calendar.patterns.T.replace( /tt/i , 'a' ).replace( /\./g, ':' ) :
                        currentCulture.calendar.patterns.t.replace( /tt/i , 'a' ).replace( /\./g, ':' ),
                    meridiemValues    : [
                        currentCulture.calendar.AM ? currentCulture.calendar.AM[0] : '',
                        currentCulture.calendar.PM ? currentCulture.calendar.PM[0] : ''
                    ],
                    allowClear: _.isUndefined(options.allowClear) || !!options.allowClear

                })),

                updateElementStyles = function(){

                    $backgroundInput.addClass( 'time-input' );
                    $backgroundInput.attr( 'id', timeInputView.timeInputId() );
                },

                timeInputAccessor = ko.observable({
                    name: TIME_INPUT_TEMPLATE,
                    data: timeInputView
                }),

                clearSubscriptions = function(){
                    _.each( subscriptions, function(subscription){
                        subscription.dispose();
                    });
                },

                updatingBindingSubscriptions = function( bindingObject ){
                    var toggleDisabled = function( value ){
                            timeInputView.isDisabled( value );
                            $backgroundInput.attr('disabled', value);
                            $backgroundInput.prop('disabled', value);
                        },
                        toggleEnable = function( value ){
                            toggleDisabled( !value );
                        },

                        bindHasAllValues = function(bindingContext) {
                            if ( !ko.isObservable( bindingContext.hasAllValues) ){
                                bindingContext.hasAllValues = ko.observable();
                            }

                            subscriptions.push(
                                timeInputView.hasAllValues.subscribe(function(){
                                    bindingContext.hasAllValues(timeInputView.hasAllValues());
                                })
                            );

                            bindingContext.hasAllValues(timeInputView.hasAllValues());
                        };

                    if (_.has( bindingObject, 'disable') ){

                        if ( ko.isObservable( bindingObject.disable ) ){
                            subscriptions.push(
                                bindingObject.disable.subscribe( toggleDisabled )
                            );
                        }

                        toggleDisabled( ko.unwrap( bindingObject.disable ) );
                    }

                    if (_.has( bindingObject, 'enable') ){

                        if ( ko.isObservable( bindingObject.enable ) ){
                            subscriptions.push(
                                bindingObject.enable.subscribe( toggleEnable )
                            );
                        }

                        toggleEnable( ko.unwrap( bindingObject.enable ) );
                    }

                    if (_.has( bindingObject, 'timeInput' ) ){
                        if ( ko.isObservable( bindingObject.timeInput.value ) ){

                            bindingObject.timeInput.value.extend( {
                                rateLimit: {
                                    timeout: 200,
                                    method: "notifyWhenChangesStop"
                                }
                            });

                            subscriptions.push(
                                bindingObject.timeInput.value.subscribe( function(){

                                    timeInputView.configure( options );
                                })
                            );
                        } else if ( ko.isObservable( bindingObject.timeInput )) {
                            bindingObject.timeInput.extend( {
                                rateLimit: {
                                    timeout: 200,
                                    method: "notifyWhenChangesStop"
                                }
                            });
                            subscriptions.push(
                                bindingObject.timeInput.subscribe( function(){

                                    timeInputView.configure( options );
                                })
                            );
                        }

                        bindHasAllValues(options);
                    }

                    if (_.has( bindingObject, 'hasAllValues' ) ){
                        bindHasAllValues(bindingObject);
                    }
                },
                transferFocusToTimeWidget = function(){
                    timeInputView.onTimeInputWidgetFocus();
                },

                onTimeInputMouseOver = function(e){
                    timeInputView.onTimeInputWidgetMouseOver();
                    e.preventDefault();
                },

                onTimeInputMouseOut = function(e){
                    timeInputView.onTimeInputWidgetMouseOut();
                    e.preventDefault();
                },

                onTimeInputClick = function(e){
                    timeInputView.onTimeInputWidgetFocus();
                    e.preventDefault();
                };

            if ( !ko.isObservable( options.value ) ) {
                throw new Error('An observable value must be used with the time input data binding');
            }

            updateElementStyles();

            $backgroundInput.on( EVENT_FOCUS      + TIME_INPUT_NAMESPACE, transferFocusToTimeWidget );
            $backgroundInput.on( EVENT_MOUSE_OVER + TIME_INPUT_NAMESPACE, onTimeInputMouseOver );
            $backgroundInput.on( EVENT_MOUSE_OUT  + TIME_INPUT_NAMESPACE, onTimeInputMouseOut  );
            $backgroundInput.on( EVENT_CLICK      + TIME_INPUT_NAMESPACE, onTimeInputClick  );


            ko.bindingHandlers.include.init.call(self, $backgroundInput[0], timeInputAccessor, allBindings, viewModel, bindingContext);
            ko.bindingHandlers.include.update.call(self, $backgroundInput[0], timeInputAccessor, allBindings, viewModel, bindingContext);


            updatingBindingSubscriptions( allBindings() );

            // Clean up the binding handler when the element is removed from the DOM.
            ko.utils.domNodeDisposal.addDisposeCallback( element, function () {
                clearSubscriptions();
                var $backgroundInput = $( element );

                if ( $backgroundInput ) {
                    $backgroundInput.off( TIME_INPUT_NAMESPACE );
                }

                if ( timeInputView ) {
                    timeInputView.dispose();
                }
            });

        }
    };
});

